import React, { useContext, useEffect, useRef, useState } from 'react'
import loadable from '@loadable/component'
import { LayoutContext } from '../../contexts'
import SectionPage from '../pageBlocks/SectionPage'

import HeroBlock from '../pageBlocks/HeroBlock'
import TileBlock from '../pageBlocks/TileBlock'
import HeadingBlock from '../pageBlocks/HeadingBlock'
import { useCategoryPageViewedTracking } from '../../hooks/tracking'

const UspBlock = loadable(() => import('../pageBlocks/UspBlock'))
const QuoteBlock = loadable(() => import('../pageBlocks/QuoteBlock'))
const VideoBlock = loadable(() => import('../pageBlocks/VideoBlock'))
const GuaranteeBlock = loadable(() => import('../pageBlocks/GuaranteeBlock'))
const InformationBlock = loadable(() =>
  import('../pageBlocks/InformationBlock'),
)
const MediaBlock = loadable(() => import('../pageBlocks/MediaBlock'))
const ReviewBlock = loadable(() => import('../pageBlocks/ReviewBlock'))
const SocialSharingBlock = loadable(() =>
  import('../pageBlocks/SocialSharingBlock'),
)
const FeaturedCollection = loadable(() =>
  import('../shared/FeaturedCollection'),
)

/**
 * To use the Related Items, from Section - Page
 * you have to map the items, rendering the final markup
 * also related items HAS to be parsed, else the section, won't render
 */

const BlockRenderer = ({
  blocks = [],
  related = [],
  rating = {},
  location,
}) => {
  const { node_locale, currency } = useContext(LayoutContext)
  const [pageInfo, setPageInfo] = useState(false) // 'true' shows 'read more' in the heading block
  const readMoreRef = useRef()
  const hasHeadingBlock =
    blocks &&
    blocks.some(({ __typename }) => __typename === 'ContentfulHeadingBlock')
  const hasTileBlock =
    blocks &&
    blocks.some(({ __typename }) => __typename === 'ContentfulTileBlock')

  const { trustpilotStars, totalNumberOfReviews } = rating

  useEffect(() => {
    if (hasTileBlock) {
      useCategoryPageViewedTracking({
        currency: currency?.code,
        locale: node_locale,
        category: blocks.find(
          ({ __typename }) => __typename === 'ContentfulTileBlock',
        ).category,
      })
    }
  }, [])

  return (blocks ?? []).map((block, index) => {
    if (!block.__typename || !block.id) return <ErrorHandler block={block} />

    switch (block.__typename) {
      case 'ContentfulSectionPage':
        return <SectionPage key={block.id} block={block} related={related} />
      case 'ContentfulHeadingBlock':
        return (
          <HeadingBlock
            title={block.title}
            pageDescription={block.pageDescription}
            cta={block.cta}
            breadcrumbs={block.breadcrumbs}
            reviewMessage={block.reviewMessage}
            partnerText={block.partnerText}
            partnerLogo={block.partnerLogo}
            partnerLogoLink={block.partnerLogoLink}
            readMoreRef={readMoreRef}
            pageInfo={pageInfo}
            trustpilotStars={trustpilotStars}
            totalNumberOfReviews={totalNumberOfReviews}
            key={block.id}
            location={location}
          />
        )
      case 'ContentfulInformationBlock':
        return (
          <InformationBlock
            readMoreRef={readMoreRef}
            setPageInfo={setPageInfo}
            information={block.information}
            key={block.id}
            position={hasHeadingBlock ? index : index + 1}
          />
        )
      case 'ContentfulVideoBlock':
        return (
          <VideoBlock
            videos={block.videos}
            title={block.title}
            key={block.id}
            videoPage={block.videoPage}
            viewType='Landing Page'
            position={hasHeadingBlock ? index : index + 1}
          />
        )
      case 'ContentfulFeaturedProductsBlock':
        return (
          <FeaturedCollection
            heading={block.title}
            featuredCollection={block.products}
            key={block.id}
            className={'landing-page'}
            location={location}
            viewType='Landing Page'
            position={hasHeadingBlock ? index : index + 1}
          />
        )
      case 'ContentfulHeroBlock':
        return (
          <HeroBlock
            key={block.id}
            heroCopyColor={block.heroCopyColor}
            heroImage={block.heroImage}
            heroImageMobile={block.heroImageMobile}
            heroCopy={block.heroCopy}
            heroVideoCopy={block.heroVideoCopy}
            heroVideo={block.heroVideo}
            heroVideoMobile={block.heroVideoMobile}
            heroVideoLink={block.heroVideoLink}
            heroButton={block.heroButton}
            heroMobileButtons={block.heroMobileButtons}
            heroVideoType={block.heroVideoType}
            location={location}
            position={hasHeadingBlock ? index : index + 1}
          />
        )
      case 'ContentfulUspBlock':
        return (
          <UspBlock
            key={block.id}
            title={block.title}
            uspVideo={block.uspVideo}
            uspSideVideo={block.uspSideVideo}
            uspAccordionItems={block.uspAccordionItems}
            position={hasHeadingBlock ? index : index + 1}
          />
        )
      case 'ContentfulQuoteBlock':
        return (
          <QuoteBlock
            key={block.id}
            title={block.title}
            quotePerson={block.quotePerson}
            quoteText={block.quoteText}
            position={hasHeadingBlock ? index : index + 1}
          />
        )
      case 'ContentfulGuaranteeBlock':
        return (
          <GuaranteeBlock
            key={block.id}
            guaranteeImage={block.guaranteeImage}
            guaranteePeriod={block.guaranteePeriod}
            guaranteeCopy={block.guaranteeCopy}
            guaranteePoints={block.guaranteePoints}
            guaranteeLink={block.guaranteeLink}
            position={hasHeadingBlock ? index : index + 1}
          />
        )
      case 'ContentfulSocialSharingBlock':
        return (
          <SocialSharingBlock
            socialSharingText={block.socialSharingText}
            socialSharingImages={block.socialSharingImages}
            socialSharingDescription={block.socialSharingDescription}
            key={block.id}
            position={hasHeadingBlock ? index : index + 1}
          />
        )
      case 'ContentfulTileBlock':
        return (
          <TileBlock
            tileLinks={block.tileLinks}
            location={location}
            key={block.id}
            position={hasHeadingBlock ? index : index + 1}
          />
        )
      case 'ContentfulMediaBlock':
        return (
          <MediaBlock
            media={block.mediaQuotes}
            key={block.id}
            position={hasHeadingBlock ? index : index + 1}
          />
        )
      case 'ContentfulReviewBlock':
        return (
          <ReviewBlock
            family={block.family}
            key={block.id}
            position={hasHeadingBlock ? index : index + 1}
          />
        )
      default:
        return null
    }
  })
}

const ErrorHandler = ({ block }) => (
  <p>
    Please add{' '}
    <strong>
      {!block.__typename && !block.id
        ? `__typename and id`
        : !block.id
        ? 'id'
        : '__typename'}
    </strong>{' '}
    to your query
  </p>
)

export default BlockRenderer
